import { computed, inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { select, Store } from '@ngrx/store';
import { map, shareReplay } from 'rxjs';

import { ChurnFlowInsightI } from '@core/churn-flow-types';
import { filterDefined } from '@core/common-legacy';
import { injectTranslate } from '@core/i18n-legacy';
import { fetchIfNotLoaded } from '@core/ngrx-legacy';
import {
  ImpactTrackerInsightsTranslationsMap,
  TellentImpactTrackerActions,
  TellentImpactTrackerSelectors,
} from '@core/tellent-data';

@Injectable()
export class ChurnFlowInsightsService {
  private readonly store = inject(Store);
  private readonly injector = inject(Injector);
  private readonly t = injectTranslate();

  private readonly selectorsMeta$ = this.store.select(TellentImpactTrackerSelectors.meta()).pipe(
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  private readonly impactTrackerInsightsData$ = this.store.pipe(
    this.fetchIfNotLoaded(),
    select(TellentImpactTrackerSelectors.byKind('recruitment')),
    filterDefined,
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  private readonly impactTrackerInsights$ = this.impactTrackerInsightsData$.pipe(
    map(data => data.insights),
  );
  private readonly insightsDataSignal = toSignal(this.impactTrackerInsights$, {
    initialValue: null,
  });

  private readonly showImpactTrackerInsights$ = this.impactTrackerInsightsData$.pipe(
    map(data => data.showMenuBanner),
  );

  private readonly cards = computed(() => {
    const data = this.insightsDataSignal();
    const map = runInInjectionContext(this.injector, ImpactTrackerInsightsTranslationsMap);

    return data
      ? data.map(({ name, values }) => {
          const { icon, title, label, formatFns, tooltip } = map[name];

          const [value1, value2] = values;
          const [value1FormatFn, value2FormatFn] = formatFns;

          return {
            icon,
            value: value1FormatFn(value1),
            title: this.t(title),
            label: this.t(label, {
              number: value2FormatFn ? value2FormatFn(value2) : value2,
            }),
            tooltip: this.t(tooltip),
          };
        })
      : [];
  });

  public readonly insightsCards = computed(() =>
    this.showImpactTrackerInsightsSignal() ? this.cards() : this.getFallbackInsights(),
  );

  public readonly showImpactTrackerInsightsSignal = toSignal(this.showImpactTrackerInsights$, {
    initialValue: false,
  });

  private getFallbackInsights(): ChurnFlowInsightI[] {
    return [
      {
        icon: 'domain',
        label: this.t('tlc.churn_flow.insights.fallback.data.1.label'),
        value: this.t('tlc.churn_flow.insights.fallback.data.1.value'),
      },
      {
        icon: 'assignment',
        label: this.t('tlc.churn_flow.insights.fallback.data.2.label'),
        value: this.t('tlc.churn_flow.insights.fallback.data.2.value'),
      },
      {
        icon: 'cash',
        label: this.t('tlc.churn_flow.insights.fallback.data.3.label'),
        value: this.t('tlc.churn_flow.insights.fallback.data.3.value'),
        tooltip: this.t('tlc.churn_flow.insights.fallback.data.3.tooltip'),
      },
      {
        icon: 'access-time',
        label: this.t('tlc.churn_flow.insights.fallback.data.4.label'),
        value: this.t('tlc.churn_flow.insights.fallback.data.4.value'),
      },
      {
        icon: 'assignment',
        label: this.t('tlc.churn_flow.insights.fallback.data.5.label'),
        value: this.t('tlc.churn_flow.insights.fallback.data.5.value'),
      },
      {
        icon: 'automated-actions',
        label: this.t('tlc.churn_flow.insights.fallback.data.6.label'),
        value: this.t('tlc.churn_flow.insights.fallback.data.6.value'),
        tooltip: this.t('tlc.churn_flow.insights.fallback.data.6.tooltip'),
      },
    ];
  }

  private fetchIfNotLoaded() {
    return fetchIfNotLoaded(this.selectorsMeta$, () =>
      this.store.dispatch(TellentImpactTrackerActions.fetchCollection()),
    );
  }
}
